<template>
    <div class="block-editor cache-delete">
        <div class="wrapper">
            <!-- Cache Key -->
            <section>
                <div class="title">{{ $t("tables.automationblock.cacheKey") }}</div>
                <TiptapEditor
                    :placeholder="$t('tables.automationblock.cacheDeleteKeyPlaceholder')"
                    :show-attributes="true"
                    v-model="clone.key"
                    automation-mode
                />
            </section>

            <!-- Use Pattern -->
            <section>
                <div class="pattern-toggle">
                    <a-checkbox v-model="clone.usePattern">
                        {{ $t("tables.automationblock.usePatternMatching") }}
                    </a-checkbox>
                </div>

                <div v-if="clone.usePattern" class="pattern-help">
                    <div class="help-title">
                        <i class="material-icons">pattern</i>
                        <span>{{ $t("tables.automationblock.patternMatchingHowItWorks") }}</span>
                    </div>
                    <div class="pattern-examples">
                        <div class="example">
                            <code>{{ $t('auto.cacheDeleteChat3') }}</code>
                            <span>{{ $t("tables.automationblock.patternExChatStar") }}</span>
                        </div>
                        <div class="example">
                            <code>{{ $t('auto.cacheDeleteChat2') }}</code>
                            <span>{{ $t("tables.automationblock.patternExStarChat") }}</span>
                        </div>
                        <div class="example">
                            <code>{{ $t('auto.cacheDeleteChat') }}</code>
                            <span>{{ $t("tables.automationblock.patternExStarChatStar") }}</span>
                        </div>
                        <div class="example">
                            <code>user_session</code>
                            <span>{{ $t("tables.automationblock.patternExExactMatch") }}</span>
                        </div>
                    </div>
                </div>

                <div v-else class="help-text">
                    <i class="material-icons">info</i>
                    <span>{{ $t("tables.automationblock.exactMatchOnlyHelp") }}</span>
                </div>
            </section>

            <!-- Warning -->
            <section v-if="clone.usePattern">
                <div class="warning-box">
                    <i class="material-icons">warning</i>
                    <div>
                        <strong>{{ $t("tables.automationblock.warningTitle") }}</strong>
                        <p>{{ $t("tables.automationblock.patternMatchingIrreversibleWarning") }}</p>
                    </div>
                </div>
            </section>

            <!-- Output Info -->
            <section>
                <div class="output-info">
                    <div class="info-title">
                        <i class="material-icons">output</i>
                        <span>{{ $t("tables.automationblock.outputInfo") }}</span>
                    </div>
                    <div class="output-attributes">
                        <div class="attribute-item">
                            <code>{{ $t('auto.cacheDeleteBlockBlockidDeletedcount') }}</code>
                            <span>{{ $t("tables.automationblock.deletedCountLabel") }}</span>
                        </div>
                        <div class="attribute-item">
                            <code>{{ $t('auto.cacheDeleteBlockBlockidSuccess') }}</code>
                            <span>{{ $t("tables.automationblock.successBooleanLabel") }}</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import _ from "lodash";
import TiptapEditor from "../../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";

export default {
    name: "CacheDelete",
    components: {
        TiptapEditor,
    },
    props: ["value"],
    data() {
        return {
            clone: _.cloneDeep(this.value || {
                key: "",
                usePattern: false,
            }),
        };
    },
    watch: {
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            },
        },
    },
};
</script>

<style lang="scss">
.block-editor.cache-delete {
    .wrapper {
        padding: 30px;

        section {
            margin-bottom: 20px;

            .title {
                margin-bottom: 10px;
                font-weight: 600;
                font-size: 16px;
                line-height: 20px;
            }

            .pattern-toggle {
                margin-bottom: 15px;
            }

            .pattern-help {
                background: #f8f9fa;
                border-radius: 8px;
                padding: 15px;
                border-left: 3px solid #6c757d;

                .help-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    margin-bottom: 12px;
                    color: #495057;

                    .material-icons {
                        font-size: 20px;
                    }
                }

                .pattern-examples {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;

                    .example {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;

                        code {
                            background: #e9ecef;
                            padding: 4px 8px;
                            border-radius: 4px;
                            font-size: 13px;
                            color: #d63384;
                            width: fit-content;
                        }

                        span {
                            font-size: 12px;
                            color: #666;
                            margin-left: 12px;
                        }
                    }
                }
            }

            .help-text {
                display: flex;
                align-items: flex-start;
                gap: 8px;
                margin-top: 8px;
                padding: 10px;
                background: #f0f8ff;
                border-radius: 6px;
                font-size: 13px;
                color: #666;

                .material-icons {
                    font-size: 18px;
                    color: #1890ff;
                }
            }

            .warning-box {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                padding: 15px;
                background: #fff3cd;
                border: 1px solid #ffc107;
                border-radius: 8px;

                .material-icons {
                    font-size: 24px;
                    color: #ff9800;
                }

                strong {
                    color: #856404;
                    font-size: 14px;
                }

                p {
                    margin: 4px 0 0 0;
                    font-size: 13px;
                    color: #856404;
                }
            }

            .output-info {
                background: #f8f9fa;
                border-radius: 8px;
                padding: 15px;
                border-left: 3px solid #28a745;

                .info-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    margin-bottom: 12px;
                    color: #28a745;

                    .material-icons {
                        font-size: 20px;
                    }
                }

                .output-attributes {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;

                    .attribute-item {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;

                        code {
                            background: #e9ecef;
                            padding: 4px 8px;
                            border-radius: 4px;
                            font-size: 13px;
                            color: #d63384;
                        }

                        span {
                            font-size: 12px;
                            color: #666;
                        }
                    }
                }
            }
        }
    }
}
</style>

